import type { ReactNode } from "react";

// Kept identical to the Admin app's `modules/loyaltyProgram/tierBadgeEmblems.tsx`.

/** Every emblem a tier badge can show, by the backend `LoyaltyTierBadge` member name. */
/** A chip leans every leaf the same way; larger sizes alternate leaves inward and outward. */
const laurelLeafRotation = (tangent: number, index: number, isChip: boolean) => {
  if (isChip) {
    return tangent - 26;
  }
  return index % 2 === 0 ? tangent - 32 : tangent + 30;
};

export const TIER_BADGE_EMBLEM_NAMES = [
  "Color",
  "Initial",
  "Star",
  "Gem",
  "Crown",
  "Heart",
  "Trophy",
  "Medal",
  "Rosette",
  "Laurel",
  "Shield",
  "Diamond",
  "Pearl",
  "Lotus",
  "Sparkle",
  "Droplet",
  "Feather",
  "Moon",
  "Sun",
] as const;

export type TierBadgeEmblemName = (typeof TIER_BADGE_EMBLEM_NAMES)[number];

/**
 * How much an emblem draws: `chip` (20px) is the silhouette and its big facets, `small` (28px)
 * adds the smaller cuts, `full` adds engraved lines and highlight edges.
 */
export type EmblemDetail = "chip" | "small" | "full";

/** Facet tones from the lit side to the shaded side. */
type Tone = "hi" | "lit" | "base" | "shade" | "deep";

export type EmblemPaint = {
  readonly tones: Readonly<Record<Tone, string>>;
  /** The metal gradient fill, lit top left. */
  readonly metal: string;
  /** The pearl's lustre fill. */
  readonly lustre: string;
  readonly edge: string;
  readonly edgeWidth: number;
  /** Highlight edges and glints. */
  readonly shine: string;
  /** The engraved initial and its offset copy. */
  readonly ink: string;
  readonly inkDepth: string;
  readonly detail: EmblemDetail;
};

type Point = readonly [number, number];
type Facet = { readonly d: string; readonly tone: Tone };

/** Light falls from the top left; a facet facing it is lit, one facing away is shaded. */
const LIGHT: Point = [-0.55, -0.835];

const toneFacing = (nx: number, ny: number): Tone => {
  const length = Math.hypot(nx, ny);
  const facing = (nx * LIGHT[0] + ny * LIGHT[1]) / length;
  if (facing > 0.55) return "hi";
  if (facing > 0.15) return "lit";
  if (facing > -0.25) return "base";
  if (facing > -0.62) return "shade";
  return "deep";
};

const round = (value: number) => Math.round(value * 100) / 100;

const polygon = (points: readonly Point[]) =>
  `M${points.map(([x, y]) => `${round(x)} ${round(y)}`).join("L")}Z`;

const polar = (center: Point, radius: number, degrees: number): Point => [
  center[0] + radius * Math.cos((degrees * Math.PI) / 180),
  center[1] + radius * Math.sin((degrees * Math.PI) / 180),
];

const scaleAbout = (points: readonly Point[], center: Point, scale: number): Point[] =>
  points.map(([x, y]) => [
    center[0] + (x - center[0]) * scale,
    center[1] + (y - center[1]) * scale,
  ]);

/**
 * The bevel between two outlines with the same number of points: one facet per edge, toned by
 * which way that edge faces. `inward` shades a recess (the facet tilts the other way).
 */
const ringFacets = (
  outer: readonly Point[],
  inner: readonly Point[],
  center: Point,
  inward = false,
): Facet[] =>
  outer.map((start, index) => {
    const next = (index + 1) % outer.length;
    const end = outer[next];
    let nx = end[1] - start[1];
    let ny = start[0] - end[0];
    const midX = (start[0] + end[0]) / 2 - center[0];
    const midY = (start[1] + end[1]) / 2 - center[1];
    if (nx * midX + ny * midY < 0) {
      nx = -nx;
      ny = -ny;
    }
    return {
      d: polygon([start, end, inner[next], inner[index]]),
      tone: inward ? toneFacing(-nx, -ny) : toneFacing(nx, ny),
    };
  });

/** A step-cut outline: bevels stepping in to a flat table, or to a point when the last scale is 0. */
const steppedFacets = (
  outline: readonly Point[],
  center: Point,
  scales: readonly number[],
  tableTone: Tone = "lit",
): Facet[] => {
  const rings = scales.map((scale) => scaleAbout(outline, center, scale));
  const bevels = rings.slice(1).flatMap((inner, index) => ringFacets(rings[index], inner, center));
  const table = rings[rings.length - 1];
  return scales[scales.length - 1] > 0
    ? [...bevels, { d: polygon(table), tone: tableTone }]
    : bevels;
};

const starOutline = (
  center: Point,
  points: number,
  outer: number,
  inner: number,
  startDegrees = -90,
): Point[] =>
  Array.from({ length: points * 2 }, (_, index) =>
    polar(center, index % 2 === 0 ? outer : inner, startDegrees + (index * 180) / points),
  );

const sampleCircle = (center: Point, radius: number, count: number, startDegrees = -90) =>
  Array.from({ length: count }, (_, index) =>
    polar(center, radius, startDegrees + (index * 360) / count),
  );

/** `count` points along the arc from `from` to `to` degrees, the way that passes `via`. */
const arcThrough = (
  center: Point,
  radius: number,
  from: number,
  to: number,
  via: number,
  count: number,
): Point[] => {
  const normalize = (degrees: number) => ((degrees % 360) + 360) % 360;
  const forward = normalize(to - from);
  const sweep = normalize(via - from) < forward ? forward : forward - 360;
  return Array.from({ length: count }, (_, index) =>
    polar(center, radius, from + (sweep * index) / (count - 1)),
  );
};

const mirror = (points: readonly Point[]): Point[] => points.map(([x, y]) => [48 - x, y]);

/* ------------------------------------------------------------------------------------------- */
/* Shared drawing pieces                                                                         */
/* ------------------------------------------------------------------------------------------- */

const Facets = ({ facets, paint }: { facets: readonly Facet[]; paint: EmblemPaint }) => (
  <g strokeLinejoin="round">
    {facets.map((facet, index) => (
      // Each facet is stroked in its own tone so neighbours meet without a seam.
      <path
        d={facet.d}
        fill={paint.tones[facet.tone]}
        key={index}
        stroke={paint.tones[facet.tone]}
        strokeWidth="0.35"
      />
    ))}
  </g>
);

const Outline = ({ d, paint }: { d: string; paint: EmblemPaint }) => (
  <path
    d={d}
    fill="none"
    stroke={paint.edge}
    strokeLinejoin="round"
    strokeOpacity="0.9"
    strokeWidth={paint.edgeWidth}
  />
);

/** A cut shape: its facets, then its outline drawn crisp over them. */
const Cut = ({
  outline,
  facets,
  paint,
}: {
  outline: readonly Point[];
  facets: readonly Facet[];
  paint: EmblemPaint;
}) => (
  <>
    <path d={polygon(outline)} fill={paint.metal} />
    <Facets facets={facets} paint={paint} />
    <Outline d={polygon(outline)} paint={paint} />
  </>
);

/** A filled metal shape with its outline. */
const Plate = ({ d, paint, fill }: { d: string; paint: EmblemPaint; fill?: string }) => (
  <path
    d={d}
    fill={fill ?? paint.metal}
    stroke={paint.edge}
    strokeLinejoin="round"
    strokeOpacity="0.9"
    strokeWidth={paint.edgeWidth}
  />
);

const Engraving = ({ d, paint }: { d: string; paint: EmblemPaint }) =>
  paint.detail === "full" ? (
    <path
      d={d}
      fill="none"
      stroke={paint.tones.deep}
      strokeLinecap="round"
      strokeOpacity="0.55"
      strokeWidth="0.6"
    />
  ) : null;

const Highlight = ({ d, paint }: { d: string; paint: EmblemPaint }) =>
  paint.detail === "full" ? (
    <path
      d={d}
      fill="none"
      stroke={paint.shine}
      strokeLinecap="round"
      strokeOpacity="0.85"
      strokeWidth="0.7"
    />
  ) : null;

/** A four-point glint cut like a small crystal. */
const Glint = ({
  center,
  radius,
  paint,
}: {
  center: Point;
  radius: number;
  paint: EmblemPaint;
}) => {
  const outline = starOutline(center, 4, radius, radius * 0.26);
  return <Cut facets={steppedFacets(outline, center, [1, 0])} outline={outline} paint={paint} />;
};

/* ------------------------------------------------------------------------------------------- */
/* Colour only                                                                                   */
/* ------------------------------------------------------------------------------------------- */

const COLOR_CENTER: Point = [24, 24];

/** A cushion-cut lozenge: the tier colour itself, a soft bevel and a flat table, no emblem. */
const COLOR_OUTLINE: Point[] = (() => {
  const corner = 4.5;
  const reach = 22;
  return [-90, 0, 90, 180].flatMap((degrees) => {
    const pivot = polar(COLOR_CENTER, reach - corner * Math.SQRT2, degrees);
    return Array.from({ length: 5 }, (_, step) => polar(pivot, corner, degrees - 45 + step * 22.5));
  });
})();

/** The left and top corners: the half a chip lights instead of cutting a bevel. */
const COLOR_LIT_HALF = [...COLOR_OUTLINE.slice(15), ...COLOR_OUTLINE.slice(0, 5)];

const ColorEmblem = ({ paint }: EmblemArgs) => (
  <>
    <Cut
      facets={
        paint.detail === "chip"
          ? [{ d: polygon(COLOR_LIT_HALF), tone: "lit" }]
          : steppedFacets(COLOR_OUTLINE, COLOR_CENTER, [1, 0.62], "base")
      }
      outline={COLOR_OUTLINE}
      paint={paint}
    />
    <Highlight d="M6 22.5L22.5 6" paint={paint} />
  </>
);

/* ------------------------------------------------------------------------------------------- */
/* Awards                                                                                        */
/* ------------------------------------------------------------------------------------------- */

const SEAL_CENTER: Point = [24, 24];
const SEAL_POINTS = 16;

/** The tier's first letter engraved on a serrated award seal. */
const InitialEmblem = ({ paint, initial }: EmblemArgs) => {
  const outer = starOutline(SEAL_CENTER, SEAL_POINTS, 22.5, 19.6);
  const rim = sampleCircle(SEAL_CENTER, 17.6, SEAL_POINTS * 2);
  const recess = sampleCircle(SEAL_CENTER, 15.6, SEAL_POINTS * 2);
  const isChip = paint.detail === "chip";
  const letterSize = isChip ? 27 : 19;
  return (
    <>
      <Cut
        facets={
          isChip
            ? [...ringFacets(outer, rim, SEAL_CENTER), { d: polygon(rim), tone: "base" }]
            : [
                ...ringFacets(outer, rim, SEAL_CENTER),
                ...ringFacets(rim, recess, SEAL_CENTER, true),
                { d: polygon(recess), tone: "base" },
              ]
        }
        outline={outer}
        paint={paint}
      />
      <Engraving d={polygon(sampleCircle(SEAL_CENTER, 13.6, 48))} paint={paint} />
      {paint.detail !== "chip" && (
        <text
          dominantBaseline="central"
          fill={paint.inkDepth}
          fontSize={letterSize}
          fontWeight={650}
          opacity={0.75}
          textAnchor="middle"
          x={24}
          y={25.4}
        >
          {initial}
        </text>
      )}
      <text
        dominantBaseline="central"
        fill={paint.ink}
        fontSize={letterSize}
        fontWeight={isChip ? 750 : 650}
        textAnchor="middle"
        x={24}
        y={isChip ? 25 : 24.6}
      >
        {initial}
      </text>
    </>
  );
};

const STAR_CENTER: Point = [24, 25.5];

/** A bevelled five-point star, each point cut into a lit and a shaded half. */
const StarEmblem = ({ paint }: EmblemArgs) => {
  const outline = starOutline(STAR_CENTER, 5, 23, 9.6);
  return (
    <Cut facets={steppedFacets(outline, STAR_CENTER, [1, 0])} outline={outline} paint={paint} />
  );
};

const CROWN_CENTER: Point = [24, 27];
const CROWN_BODY: Point[] = [
  [7, 35],
  [4.5, 13.5],
  [15, 23.5],
  [24, 9],
  [33, 23.5],
  [43.5, 13.5],
  [41, 35],
];
const CROWN_BAND = "M6 34.5H42V42H6Z";

const CrownEmblem = ({ paint }: EmblemArgs) => (
  <>
    <Cut
      facets={steppedFacets(CROWN_BODY, CROWN_CENTER, [1, 0.62], "base")}
      outline={CROWN_BODY}
      paint={paint}
    />
    {(
      [
        [4.5, 11.6, 2.9],
        [24, 7, 3.1],
        [43.5, 11.6, 2.9],
      ] as const
    ).map(([cx, cy, r]) => (
      <circle
        cx={cx}
        cy={cy}
        fill={paint.tones.hi}
        key={cx}
        r={r}
        stroke={paint.edge}
        strokeOpacity="0.9"
        strokeWidth={paint.edgeWidth}
      />
    ))}
    <Plate d={CROWN_BAND} paint={paint} />
    <path d="M6 38.25H42V42H6Z" fill={paint.tones.shade} />
    <Outline d={CROWN_BAND} paint={paint} />
    {paint.detail !== "chip" && (
      <>
        <path d="M24 35.6L26.4 38.25L24 40.9L21.6 38.25Z" fill={paint.tones.hi} />
        <circle cx={14} cy={38.25} fill={paint.tones.deep} r={1.6} />
        <circle cx={34} cy={38.25} fill={paint.tones.deep} r={1.6} />
      </>
    )}
    <Highlight d="M7 35.4H41" paint={paint} />
  </>
);

const TROPHY_BOWL = "M10 5H38V11C38 21.5 32 28 24 28C16 28 10 21.5 10 11Z";
const TROPHY_BOWL_SHADE = "M24 8.6H38V11C38 21.5 32 28 24 28Z";
const TROPHY_HANDLE = "M10.6 9.5C2.6 9.5 2.6 22 13.5 22.5";
const TROPHY_PLINTH = "M17.5 35H30.5L31.5 38.5H16.5Z";
const TROPHY_BASE = "M14.5 38.5H33.5L35.5 44.5H12.5Z";
const TROPHY_STAR_CENTER: Point = [24, 16.2];

const TrophyEmblem = ({ paint }: EmblemArgs) => {
  const handleWidth = paint.detail === "chip" ? 3.6 : 2.6;
  const star = starOutline(TROPHY_STAR_CENTER, 5, 5.6, 2.4);
  return (
    <>
      {[TROPHY_HANDLE, `M37.4 9.5C45.4 9.5 45.4 22 34.5 22.5`].map((d) => (
        <g fill="none" key={d} strokeLinecap="round">
          <path d={d} stroke={paint.edge} strokeOpacity="0.9" strokeWidth={handleWidth + 1.4} />
          <path d={d} stroke={paint.tones.lit} strokeWidth={handleWidth} />
        </g>
      ))}
      <Plate d="M21.5 27H26.5L25.6 33H22.4Z" fill={paint.tones.shade} paint={paint} />
      <Plate d="M19 32.5H29L28 35.2H20Z" paint={paint} />
      <Plate d={TROPHY_PLINTH} fill={paint.tones.base} paint={paint} />
      <Plate d={TROPHY_BASE} paint={paint} />
      <path d="M24 38.5H33.5L35.5 44.5H24Z" fill={paint.tones.shade} />
      <Outline d={TROPHY_BASE} paint={paint} />
      <Plate d={TROPHY_BOWL} paint={paint} />
      <path d={TROPHY_BOWL_SHADE} fill={paint.tones.shade} />
      <path d="M10 5H38V8.6H10Z" fill={paint.tones.hi} />
      <Outline d={TROPHY_BOWL} paint={paint} />
      {paint.detail !== "chip" && (
        <Cut
          facets={steppedFacets(star, TROPHY_STAR_CENTER, [1, 0])}
          outline={star}
          paint={{ ...paint, edgeWidth: paint.edgeWidth * 0.6 }}
        />
      )}
      <Engraving d="M11 8.6H37" paint={paint} />
      <Highlight d="M13.2 11C13.4 18 16.4 23 20 25" paint={paint} />
    </>
  );
};

const MEDAL_CENTER: Point = [24, 32];
const MEDAL_STAR_CENTER: Point = [24, 32.4];

const MedalEmblem = ({ paint }: EmblemArgs) => {
  const rim = sampleCircle(MEDAL_CENTER, 13, 24);
  const face = sampleCircle(MEDAL_CENTER, 10, 24);
  const star = starOutline(MEDAL_STAR_CENTER, 5, 6.6, 2.8);
  return (
    <>
      <Plate d="M29 1H38.5L28.5 22.5H20Z" fill={paint.tones.deep} paint={paint} />
      <Plate d="M9.5 1H19L28 22.5H19.5Z" fill={paint.tones.shade} paint={paint} />
      {paint.detail !== "chip" && <path d="M13 1H15.6L24.4 22.5H21.8Z" fill={paint.tones.hi} />}
      <Plate d="M20.5 18.5H27.5V22H20.5Z" fill={paint.tones.base} paint={paint} />
      <Cut
        facets={[...ringFacets(rim, face, MEDAL_CENTER), { d: polygon(face), tone: "base" }]}
        outline={rim}
        paint={paint}
      />
      {paint.detail === "chip" ? (
        <path d={polygon(star)} fill={paint.tones.hi} />
      ) : (
        <Cut
          facets={steppedFacets(star, MEDAL_STAR_CENTER, [1, 0])}
          outline={star}
          paint={{ ...paint, edgeWidth: paint.edgeWidth * 0.6 }}
        />
      )}
    </>
  );
};

const ROSETTE_CENTER: Point = [24, 18.5];
const ROSETTE_PLEATS = 16;

/** A pleated ring: alternate wedges catch and lose the light. */
const pleats = (outerRadius: number, innerRadius: number, depth: number, offset: number) => {
  const samplesPerPleat = 4;
  const count = ROSETTE_PLEATS * samplesPerPleat;
  const edge = Array.from({ length: count }, (_, index) => {
    const degrees = -90 + (index * 360) / count + offset;
    const scallop = Math.abs(Math.sin((index * Math.PI) / samplesPerPleat));
    return polar(ROSETTE_CENTER, outerRadius - depth * (1 - scallop), degrees);
  });
  const wedges: Facet[] = Array.from({ length: ROSETTE_PLEATS }, (_, pleat) => {
    const start = pleat * samplesPerPleat;
    const points = Array.from(
      { length: samplesPerPleat + 1 },
      (_, step) => edge[(start + step) % count],
    );
    const inner = [
      polar(ROSETTE_CENTER, innerRadius, -90 + ((start + samplesPerPleat) * 360) / count + offset),
      polar(ROSETTE_CENTER, innerRadius, -90 + (start * 360) / count + offset),
    ];
    return { d: polygon([...points, ...inner]), tone: pleat % 2 === 0 ? "lit" : "shade" };
  });
  return { edge, wedges };
};

const RosetteEmblem = ({ paint }: EmblemArgs) => {
  const outer = pleats(16, 6, 1.8, 0);
  const inner = pleats(11.4, 6, 1.4, 360 / ROSETTE_PLEATS / 2);
  const button = sampleCircle(ROSETTE_CENTER, 6.6, 16);
  return (
    <>
      <Plate
        d="M18 25L25 28.5L20.5 46.5L17 42.5L12.5 44.5Z"
        fill={paint.tones.shade}
        paint={paint}
      />
      <Plate
        d="M30 25L23 28.5L27.5 46.5L31 42.5L35.5 44.5Z"
        fill={paint.tones.deep}
        paint={paint}
      />
      <Highlight d="M19.8 29.5L16.6 41.6" paint={paint} />
      <path d={polygon(outer.edge)} fill={paint.metal} />
      <Facets facets={outer.wedges} paint={paint} />
      <Outline d={polygon(outer.edge)} paint={paint} />
      {paint.detail !== "chip" && (
        <path
          d={polygon(inner.edge)}
          fill={paint.tones.shade}
          stroke={paint.edge}
          strokeOpacity="0.6"
          strokeWidth={paint.edgeWidth * 0.6}
        />
      )}
      <Cut
        facets={steppedFacets(button, ROSETTE_CENTER, [1, 0.6], "base")}
        outline={button}
        paint={paint}
      />
    </>
  );
};

const LAUREL_CENTER: Point = [24, 23];
const LAUREL_RADIUS = 16.5;

/** One side of the wreath; the other is its mirror image. */
const LaurelBranch = ({ paint }: { paint: EmblemPaint }) => {
  const isChip = paint.detail === "chip";
  const angles = isChip
    ? [112, 146, 180, 214, 246]
    : [108, 122, 136, 150, 164, 178, 192, 206, 220, 234, 250];
  const length = isChip ? 12 : 7.6;
  const width = isChip ? 4.6 : 2.8;
  const stem = arcThrough(LAUREL_CENTER, LAUREL_RADIUS, 96, 250, 180, 24);
  return (
    <>
      <path
        d={`M${stem.map(([x, y]) => `${round(x)} ${round(y)}`).join("L")}`}
        fill="none"
        stroke={paint.tones.deep}
        strokeLinecap="round"
        strokeWidth={isChip ? 2.6 : 1.3}
      />
      {angles.map((degrees, index) => {
        const [x, y] = polar(LAUREL_CENTER, LAUREL_RADIUS, degrees);
        const tangent = degrees + 90;
        // A chip leans every leaf outward so the two branches read at a glance.
        const rotation = laurelLeafRotation(tangent, index, isChip);
        const half = width;
        return (
          <g
            key={degrees}
            transform={`translate(${round(x)} ${round(y)}) rotate(${round(rotation)})`}
          >
            <path
              d={`M0 0Q${length / 2} ${-half} ${length} 0Z`}
              fill={paint.tones.lit}
              stroke={paint.tones.lit}
              strokeWidth="0.3"
            />
            <path
              d={`M0 0L${length} 0Q${length / 2} ${half} 0 0Z`}
              fill={paint.tones.shade}
              stroke={paint.tones.shade}
              strokeWidth="0.3"
            />
            <path
              d={`M0 0Q${length / 2} ${-half} ${length} 0Q${length / 2} ${half} 0 0Z`}
              fill="none"
              stroke={paint.edge}
              strokeLinejoin="round"
              strokeOpacity="0.85"
              strokeWidth={paint.edgeWidth * 0.75}
            />
          </g>
        );
      })}
    </>
  );
};

const LaurelEmblem = ({ paint }: EmblemArgs) => (
  <>
    <LaurelBranch paint={paint} />
    <g transform="matrix(-1 0 0 1 48 0)">
      <LaurelBranch paint={paint} />
    </g>
    <Plate
      d="M19.5 37L24 40.5L28.5 37L29.5 44.5L24 42L18.5 44.5Z"
      fill={paint.tones.base}
      paint={paint}
    />
  </>
);

/** The left half of the shield, from the top centre down to the point. */
const SHIELD_LEFT: Point[] = (() => {
  const top = Array.from({ length: 5 }, (_, index): Point => {
    const t = index / 4;
    // Quadratic from (24,3.6) out to (7,6.5) with control (15,3.2).
    const x = (1 - t) ** 2 * 24 + 2 * (1 - t) * t * 15 + t ** 2 * 7;
    const y = (1 - t) ** 2 * 3.6 + 2 * (1 - t) * t * 3.2 + t ** 2 * 6.5;
    return [x, y];
  });
  const side = Array.from({ length: 7 }, (_, index): Point => {
    const t = (index + 1) / 7;
    // Cubic from (7,21) to the point (24,45.5).
    const x = (1 - t) ** 3 * 7 + 3 * (1 - t) ** 2 * t * 7 + 3 * (1 - t) * t ** 2 * 14 + t ** 3 * 24;
    const y =
      (1 - t) ** 3 * 21 + 3 * (1 - t) ** 2 * t * 33 + 3 * (1 - t) * t ** 2 * 40 + t ** 3 * 45.5;
    return [x, y];
  });
  return [...top, [7, 14], [7, 21], ...side];
})();
const SHIELD_CENTER: Point = [24, 22];
const SHIELD_OUTLINE: Point[] = [...SHIELD_LEFT, ...mirror(SHIELD_LEFT).reverse().slice(1, -1)];

const ShieldEmblem = ({ paint }: EmblemArgs) => {
  const field = scaleAbout(SHIELD_OUTLINE, SHIELD_CENTER, 0.74);
  const fieldLeft = scaleAbout(SHIELD_LEFT, SHIELD_CENTER, 0.74);
  return (
    <>
      <Cut
        facets={[
          ...ringFacets(SHIELD_OUTLINE, field, SHIELD_CENTER),
          { d: polygon(field), tone: "shade" },
          { d: polygon(fieldLeft), tone: "lit" },
        ]}
        outline={SHIELD_OUTLINE}
        paint={paint}
      />
      <Engraving d={polygon(field)} paint={paint} />
      <Highlight d="M8.4 8L8.4 21C8.4 30 13 36.5 19 41" paint={paint} />
    </>
  );
};

/* ------------------------------------------------------------------------------------------- */
/* Gems                                                                                          */
/* ------------------------------------------------------------------------------------------- */

const GEM_OUTLINE: Point[] = [
  [15, 6],
  [33, 6],
  [45, 18],
  [24, 44.5],
  [3, 18],
];

/** A round brilliant from the side: table, crown and pavilion. */
const GemEmblem = ({ paint }: EmblemArgs) => {
  const isChip = paint.detail === "chip";
  const facets: Facet[] = isChip
    ? [
        {
          d: polygon([
            [15, 6],
            [33, 6],
            [45, 18],
            [3, 18],
          ]),
          tone: "hi",
        },
        {
          d: polygon([
            [3, 18],
            [24, 18],
            [24, 44.5],
          ]),
          tone: "lit",
        },
        {
          d: polygon([
            [24, 18],
            [45, 18],
            [24, 44.5],
          ]),
          tone: "shade",
        },
      ]
    : [
        {
          d: polygon([
            [3, 18],
            [15, 6],
            [13, 18],
          ]),
          tone: "lit",
        },
        {
          d: polygon([
            [15, 6],
            [24, 18],
            [13, 18],
          ]),
          tone: "hi",
        },
        {
          d: polygon([
            [15, 6],
            [33, 6],
            [24, 18],
          ]),
          tone: "lit",
        },
        {
          d: polygon([
            [33, 6],
            [35, 18],
            [24, 18],
          ]),
          tone: "base",
        },
        {
          d: polygon([
            [33, 6],
            [45, 18],
            [35, 18],
          ]),
          tone: "shade",
        },
        {
          d: polygon([
            [3, 18],
            [13, 18],
            [24, 44.5],
          ]),
          tone: "lit",
        },
        {
          d: polygon([
            [13, 18],
            [24, 18],
            [24, 44.5],
          ]),
          tone: "base",
        },
        {
          d: polygon([
            [24, 18],
            [35, 18],
            [24, 44.5],
          ]),
          tone: "shade",
        },
        {
          d: polygon([
            [35, 18],
            [45, 18],
            [24, 44.5],
          ]),
          tone: "deep",
        },
      ];
  return (
    <>
      <Cut facets={facets} outline={GEM_OUTLINE} paint={paint} />
      <Highlight d="M4.5 17.6L15.4 6.6H32" paint={paint} />
      {paint.detail === "full" && <Glint center={[13.5, 11.5]} paint={paint} radius={3.2} />}
    </>
  );
};

const DIAMOND_CENTER: Point = [24, 24];
const DIAMOND_OUTLINE: Point[] = [
  [17, 3.5],
  [31, 3.5],
  [38.5, 11],
  [38.5, 37],
  [31, 44.5],
  [17, 44.5],
  [9.5, 37],
  [9.5, 11],
];

/** An emerald cut: an octagon stepping down to a long table. */
const DiamondEmblem = ({ paint }: EmblemArgs) => (
  <>
    <Cut
      facets={steppedFacets(
        DIAMOND_OUTLINE,
        DIAMOND_CENTER,
        paint.detail === "chip" ? [1, 0.55] : [1, 0.8, 0.6],
        "base",
      )}
      outline={DIAMOND_OUTLINE}
      paint={paint}
    />
    <Highlight d="M10.4 36.4V11.4L17.4 4.4H30.6" paint={paint} />
  </>
);

const PEARL_CENTER: Point = [24, 28.5];
const PEARL_RADIUS = 15.5;

/** A pearl hanging from a small pendant cap and bail. */
const PearlEmblem = ({ paint }: EmblemArgs) => (
  <>
    <circle
      cx={24}
      cy={5}
      fill="none"
      r={2.8}
      stroke={paint.edge}
      strokeOpacity="0.9"
      strokeWidth={paint.detail === "chip" ? 3 : 2.4}
    />
    {paint.detail !== "chip" && (
      <circle cx={24} cy={5} fill="none" r={2.8} stroke={paint.tones.lit} strokeWidth={1.2} />
    )}
    <circle
      cx={PEARL_CENTER[0]}
      cy={PEARL_CENTER[1]}
      fill={paint.lustre}
      r={PEARL_RADIUS}
      stroke={paint.edge}
      strokeOpacity="0.75"
      strokeWidth={paint.edgeWidth}
    />
    {paint.detail === "full" && (
      <path
        d="M35.5 34C33.6 38.8 29.2 42 24 42.2"
        fill="none"
        stroke={paint.shine}
        strokeLinecap="round"
        strokeOpacity="0.7"
        strokeWidth="1.2"
      />
    )}
    {paint.detail !== "chip" && (
      <ellipse
        cx={18.6}
        cy={22.6}
        fill="#FFFFFF"
        opacity="0.85"
        rx={3.4}
        ry={2.2}
        transform="rotate(-35 18.6 22.6)"
      />
    )}
    <Plate d="M17.2 14.6Q24 8.2 30.8 14.6L29 16.6Q24 13.4 19 16.6Z" paint={paint} />
  </>
);

/* ------------------------------------------------------------------------------------------- */
/* Beauty and wellness                                                                           */
/* ------------------------------------------------------------------------------------------- */

const LOTUS_BASE: Point = [24, 38.5];

/** A broad petal from its base at the origin up to its tip, swelling to `width` either side. */
const halfPetal = (length: number, width: number, side: 1 | -1) =>
  `M0 0C${side * 1.25 * width} ${-0.3 * length} ${side * 0.62 * width} ${-0.82 * length} 0 ${-length}`;

const petalPath = (length: number, width: number) =>
  `${halfPetal(length, width, -1)}C${0.62 * width} ${-0.82 * length} ${1.25 * width} ${-0.3 * length} 0 0Z`;

const Petal = ({
  length,
  width,
  rotation,
  paint,
}: {
  length: number;
  width: number;
  rotation: number;
  paint: EmblemPaint;
}) => (
  <g transform={`translate(${LOTUS_BASE[0]} ${LOTUS_BASE[1]}) rotate(${rotation})`}>
    <path
      d={`${halfPetal(length, width, -1)}Z`}
      fill={rotation > 0 ? paint.tones.base : paint.tones.lit}
    />
    <path
      d={`${halfPetal(length, width, 1)}Z`}
      fill={rotation < 0 ? paint.tones.base : paint.tones.shade}
    />
    <path
      d={petalPath(length, width)}
      fill="none"
      stroke={paint.edge}
      strokeLinejoin="round"
      strokeOpacity="0.9"
      strokeWidth={paint.edgeWidth}
    />
    <Engraving d={`M0 -1.5L0 ${-length + 3}`} paint={paint} />
  </g>
);

const LotusEmblem = ({ paint }: EmblemArgs) => (
  <>
    <Petal length={19} paint={paint} rotation={-62} width={7.5} />
    <Petal length={19} paint={paint} rotation={62} width={7.5} />
    <Petal length={25} paint={paint} rotation={-30} width={9.5} />
    <Petal length={25} paint={paint} rotation={30} width={9.5} />
    <Petal length={29} paint={paint} rotation={0} width={10.5} />
    <Plate
      d="M8 38Q24 44.5 40 38L37.5 41.5Q24 47.5 10.5 41.5Z"
      fill={paint.tones.deep}
      paint={paint}
    />
  </>
);

const SPARKLE_CENTER: Point = [21, 26.5];

const SparkleEmblem = ({ paint }: EmblemArgs) => {
  const outline: Point[] = [
    polar(SPARKLE_CENTER, 21, -90),
    polar(SPARKLE_CENTER, 4.6, -45),
    polar(SPARKLE_CENTER, 18, 0),
    polar(SPARKLE_CENTER, 4.6, 45),
    polar(SPARKLE_CENTER, 21, 90),
    polar(SPARKLE_CENTER, 4.6, 135),
    polar(SPARKLE_CENTER, 18, 180),
    polar(SPARKLE_CENTER, 4.6, 225),
  ];
  return (
    <>
      <Cut
        facets={steppedFacets(outline, SPARKLE_CENTER, [1, 0])}
        outline={outline}
        paint={paint}
      />
      <Glint center={[38.5, 9.5]} paint={paint} radius={paint.detail === "chip" ? 8 : 7} />
      {paint.detail !== "chip" && <Glint center={[39.5, 37.5]} paint={paint} radius={4.6} />}
    </>
  );
};

const HEART_CENTER: Point = [24, 22];
const HEART_OUTLINE: Point[] = (() => {
  const count = 32;
  const raw = Array.from({ length: count }, (_, index): Point => {
    const t = (index / count) * Math.PI * 2;
    return [
      16 * Math.sin(t) ** 3,
      -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)),
    ];
  });
  // The curve spans x -16..16 and y -12..17; fit it to 4..44 by 6..44.
  return raw.map(([x, y]): Point => [24 + x * 1.25, 21 + y * 1.3]);
})();

const HeartEmblem = ({ paint }: EmblemArgs) => (
  <>
    <Cut
      facets={steppedFacets(HEART_OUTLINE, HEART_CENTER, [1, 0.56])}
      outline={HEART_OUTLINE}
      paint={paint}
    />
    <Highlight d="M8 11.5C10 7.5 15.5 6.5 19.5 9.5" paint={paint} />
  </>
);

const DROP_CENTER: Point = [24, 29.5];
const DROP_RADIUS = 14.5;
const DROP_TIP: Point = [24, 2.5];
const DROP_OUTLINE: Point[] = (() => {
  const reach = (Math.acos(DROP_RADIUS / (DROP_CENTER[1] - DROP_TIP[1])) * 180) / Math.PI;
  return [DROP_TIP, ...arcThrough(DROP_CENTER, DROP_RADIUS, -90 + reach, 270 - reach, 90, 14)];
})();

/** A briolette drop: faceted all round. */
const DropletEmblem = ({ paint }: EmblemArgs) => (
  <>
    <Cut
      facets={steppedFacets(DROP_OUTLINE, [24, 28], [1, 0.52])}
      outline={DROP_OUTLINE}
      paint={paint}
    />
    <Highlight d="M21.6 8.5L12.6 22.5" paint={paint} />
  </>
);

const FEATHER_LEFT_VANE = "M0 -21C-5 -18 -8.5 -10 -8.5 -3L-5 -2L-8.2 1.5C-7.8 6 -5 10.5 0 12.5Z";
const FEATHER_RIGHT_VANE = "M0 -21C5 -17.5 8.2 -12 8.4 -6.5L5.2 -5L8.5 -2.5C8.5 4 5.5 10 0 12.5Z";

const FeatherEmblem = ({ paint }: EmblemArgs) => (
  <g transform="translate(24 24) rotate(38) scale(1.12)">
    <path
      d="M0 10V22"
      stroke={paint.edge}
      strokeLinecap="round"
      strokeOpacity="0.9"
      strokeWidth={paint.detail === "chip" ? 3.6 : 2.8}
    />
    {paint.detail !== "chip" && (
      <path d="M0 10V21.8" stroke={paint.tones.lit} strokeLinecap="round" strokeWidth="1.2" />
    )}
    <Plate d={FEATHER_LEFT_VANE} fill={paint.tones.lit} paint={paint} />
    <Plate d={FEATHER_RIGHT_VANE} fill={paint.tones.shade} paint={paint} />
    {paint.detail !== "chip" && (
      <path d="M0 -19V12" stroke={paint.tones.deep} strokeLinecap="round" strokeWidth="0.9" />
    )}
    <Engraving
      d="M-0.5 -12L-6 -7.5M-0.5 -5L-7 0M-0.5 3L-5.5 7.5M0.5 -14L6 -10.5M0.5 -1L7 1.5M0.5 5L5.5 8"
      paint={paint}
    />
  </g>
);

const MOON_OUTER_CENTER: Point = [22, 25];
const MOON_OUTER_RADIUS = 19.5;
const MOON_CUT_CENTER: Point = [31, 18.5];
const MOON_CUT_RADIUS = 15.5;

/** The crescent's outline: the outer circle's far arc, then the cut circle's arc back. */
const crescent = (outerRadius: number, cutRadius: number, count: number): Point[] => {
  const [ax, ay] = MOON_OUTER_CENTER;
  const [bx, by] = MOON_CUT_CENTER;
  const distance = Math.hypot(bx - ax, by - ay);
  const along = (outerRadius ** 2 - cutRadius ** 2 + distance ** 2) / (2 * distance);
  const across = Math.sqrt(outerRadius ** 2 - along ** 2);
  const ux = (bx - ax) / distance;
  const uy = (by - ay) / distance;
  const first: Point = [ax + along * ux - across * uy, ay + along * uy + across * ux];
  const second: Point = [ax + along * ux + across * uy, ay + along * uy - across * ux];
  const angle = (center: Point, [x, y]: Point) =>
    (Math.atan2(y - center[1], x - center[0]) * 180) / Math.PI;
  const away = (Math.atan2(-uy, -ux) * 180) / Math.PI;
  const toward = away + 180;
  return [
    ...arcThrough(
      MOON_OUTER_CENTER,
      outerRadius,
      angle(MOON_OUTER_CENTER, first),
      angle(MOON_OUTER_CENTER, second),
      away,
      count,
    ),
    ...arcThrough(
      MOON_CUT_CENTER,
      cutRadius,
      angle(MOON_CUT_CENTER, second),
      angle(MOON_CUT_CENTER, first),
      toward + 180,
      count,
    ).slice(1, -1),
  ];
};

const MoonEmblem = ({ paint }: EmblemArgs) => {
  const count = paint.detail === "chip" ? 10 : 16;
  const outline = crescent(MOON_OUTER_RADIUS, MOON_CUT_RADIUS, count);
  const inner = crescent(MOON_OUTER_RADIUS - 3, MOON_CUT_RADIUS + 3, count);
  const facets =
    inner.length === outline.length
      ? [...ringFacets(outline, inner, [16, 30]), { d: polygon(inner), tone: "base" as const }]
      : [];
  return (
    <>
      <Cut facets={facets} outline={outline} paint={paint} />
      {paint.detail !== "chip" && <Glint center={[36, 30]} paint={paint} radius={5.4} />}
    </>
  );
};

const SUN_CENTER: Point = [24, 24];
const SUN_RAYS = 16;
const SUN_RAY_BASE = 9;

/** Alternate long and short rays, each a lit and a shaded half. */
const sunRay = (ray: number) => {
  const degrees = -90 + (ray * 360) / SUN_RAYS;
  const isLong = ray % 2 === 0;
  const spread = isLong ? 15 : 11;
  return {
    left: polar(SUN_CENTER, SUN_RAY_BASE, degrees - spread),
    middle: polar(SUN_CENTER, SUN_RAY_BASE, degrees),
    right: polar(SUN_CENTER, SUN_RAY_BASE, degrees + spread),
    tip: polar(SUN_CENTER, isLong ? 22.5 : 16, degrees),
  };
};

const SunEmblem = ({ paint }: EmblemArgs) => {
  const disc = sampleCircle(SUN_CENTER, 9.5, 16);
  const rays = Array.from({ length: SUN_RAYS }, (_, ray) => sunRay(ray));
  return (
    <>
      {rays.map(({ left, tip, right }) => (
        <Outline d={polygon([left, tip, right])} key={polygon([tip])} paint={paint} />
      ))}
      <Facets
        facets={rays.flatMap(({ left, middle, right, tip }) => [
          ...ringFacets([left, tip], [middle, middle], SUN_CENTER).slice(0, 1),
          ...ringFacets([tip, right], [middle, middle], SUN_CENTER).slice(0, 1),
        ])}
        paint={paint}
      />
      <Cut
        facets={steppedFacets(disc, SUN_CENTER, [1, 0.64], "base")}
        outline={disc}
        paint={paint}
      />
    </>
  );
};

/* ------------------------------------------------------------------------------------------- */

type EmblemArgs = { readonly paint: EmblemPaint; readonly initial: string };

const EMBLEMS = {
  Color: ColorEmblem,
  Initial: InitialEmblem,
  Star: StarEmblem,
  Gem: GemEmblem,
  Crown: CrownEmblem,
  Heart: HeartEmblem,
  Trophy: TrophyEmblem,
  Medal: MedalEmblem,
  Rosette: RosetteEmblem,
  Laurel: LaurelEmblem,
  Shield: ShieldEmblem,
  Diamond: DiamondEmblem,
  Pearl: PearlEmblem,
  Lotus: LotusEmblem,
  Sparkle: SparkleEmblem,
  Droplet: DropletEmblem,
  Feather: FeatherEmblem,
  Moon: MoonEmblem,
  Sun: SunEmblem,
} as const satisfies Record<TierBadgeEmblemName, (args: EmblemArgs) => ReactNode>;

/** Draws the named emblem in a 48-unit box. */
export const TierBadgeEmblem = ({
  name,
  paint,
  initial,
}: {
  readonly name: TierBadgeEmblemName;
  readonly paint: EmblemPaint;
  readonly initial: string;
}) => {
  const Emblem = EMBLEMS[name];
  return <Emblem initial={initial} paint={paint} />;
};
